import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import APIService from '../../../Services/APIService';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import SearchBox from '../../FlowBuilder/Common/SearchBox/index.tsx';
import CommonTable from '../../Shared/Tables/TableV1.tsx';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import Pagination from '../../FlowBuilder/Common/Pagination/index.tsx';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import useDebounce from '../../Shared/Delay/index.tsx';

const SupplierTypeList = () => {
  const navigate = useNavigate();
  const [supplierData, setSupplierData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    page: 0,
    totalCount: 0,
    limit: 10,
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const { showSnackBar } = useSnackBar();
  const debouncedSearch = useDebounce(filters.search, 500);

  const columns = [
    {
      accessorKey: 'index',
      header: 'S.No.',
      Cell: ({ row }) => row.index + 1,
      enableSorting: false,
      size: 50,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      enableSorting: false,
      size: 50,
    },
    {
      accessorKey: 'action',
      header: 'Action',
      size: 50,
      Cell: ({ row }) => {
        const { id } = row.original;
        return (
          <Box display="flex" justifyContent="flex-start" gap={1}>
            <Link to={`/r/supplier/base-type/create/${id}`}>
              <IconButton size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Link>

            <IconButton onClick={() => onRemoveBaseSupplierType(id)} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
      enableSorting: false,
    },
  ];

  const getBaseSupplierTypeList = async (data) => {
    setIsLoading(true);
    let res = await APIService.get(
      `/v0/ui/dynamic-suppliers/base-supplier-type/?next_page=${data?.page + 1}&search=${data?.search}&limit=${data?.limit}`
    );
    if (res.status == 200) {
      setFilters({
        ...filters,
        search: data?.search,
        page: data?.page,
        totalCount: res?.data?.count,
        limit: data?.limit,
      });
      const newArray = Object.values(res?.data?.list);
      setSupplierData(newArray);
    }
    setIsLoading(false);
  };

  const onRemoveBaseSupplierType = (id) => {
    setSelectedId(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = async (confirmed) => {
    setConfirmDialogOpen(false);

    if (confirmed && selectedId) {
      let res = await APIService.remove(
        `/v0/ui/dynamic-suppliers/base-supplier-type/${selectedId}/`
      );
      if (res.status === 200) {
        const newArray = supplierData.filter((item) => item.id !== selectedId);
        setSupplierData(newArray);
        showSnackBar('Base Supplier Type deleted successfully', 'success');
      }
    }

    setSelectedId(null);
  };

  const onSearchFilterChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      search: value,
      page: 0,
    }));
  };

  useEffect(() => {
    getBaseSupplierTypeList({ ...filters, search: debouncedSearch });
  }, [debouncedSearch]);

  const handleChangePage = (event, newPage) => {
    getBaseSupplierTypeList({ ...filters, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    getBaseSupplierTypeList({ ...filters, page: 0, limit: newLimit });
  };

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '5px' }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" fontWeight="bold">
            Standard Template List
          </Typography>
        </Grid>
      </Grid>
      <Grid container sx={{ alignItems: 'center', pt: 2, pb: 2.5, gap: 2 }}>
        <Grid item xs={12} sm={6} md={2}>
          <SearchBox handleSearch={onSearchFilterChange} value={filters?.search} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => {
              navigate('/r/supplier/base-type/create');
            }}
          >
            Create Base Supplier Type
          </Button>
        </Grid>
      </Grid>
      <CommonTable
        columns={columns}
        data={supplierData}
        isLoading={isLoading}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0 }}>
            <Pagination
              totalCount={filters?.totalCount}
              rowsPerPage={filters?.limit}
              page={filters?.page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
      <ConfirmBox
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        options={{
          title: 'Confirm Deletion',
          text: 'Are you sure you want to delete this base supplier type?',
          confirmButtonText: 'Yes, Delete',
          cancelButtonText: 'Cancel',
          confirmButtonColor: 'error',
        }}
      />
    </Box>
  );
};

export default SupplierTypeList;
